import { Button } from '@toggle/design-system';
import { EarningsScenarioResponse, MappedEntity } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { TabsType } from '~/modules/tab-content/types';

import { EarningState, Expectation } from '../../types';
import { getEarningState, getScenario } from '../../utils';
import { EarningsChart } from '../earnings-chart/EarningsChart';
import { ExpectationReported } from '../expectation-reported/ExpectationReported';
import { ExpectationsSwitcher } from '../expectations-switcher/ExpectationsSwitcher';
import * as S from './EarningsWidget.styles';

export interface EarningsWidgetProps {
  earningsData: EarningsScenarioResponse;
  entity: MappedEntity;
}

export const EarningsWidget = ({
  earningsData,
  entity,
}: EarningsWidgetProps) => {
  const { t } = useTranslation('earnings');
  const [expectation, setExpectation] = useState<Expectation>(Expectation.Beat);
  const { reported, currency, eps } = earningsData;
  const { navigateToPage } = useNavigateToAssetOverview();

  const scenario = getScenario(earningsData, expectation);
  const searchParams = new URLSearchParams();

  if (eps.surprise) {
    searchParams.append(
      'epsFilter',
      getEarningState(eps) === EarningState.Beat ? 'beats' : 'misses'
    );
    searchParams.append('epsValue', eps.surprise.toString());
  }

  return (
    <S.EarningsWidgetRoot data-testid="earnings-widget">
      {reported && <ExpectationReported earning={eps} currency={currency} />}

      {!reported && (
        <ExpectationsSwitcher
          expectation={expectation}
          onChange={newExpectation => setExpectation(newExpectation)}
        />
      )}

      <EarningsChart expectation={expectation} earningsData={earningsData} />
      {scenario && (
        <Button
          data-testid="dig-into-button"
          type="button"
          size="small"
          label={t('earnings:digIntoTheData')}
          onClick={() => {
            navigateToPage({
              entityTag: entity.tag,
              tab: TabsType.Earnings,
              searchParams,
            });
          }}
        />
      )}
    </S.EarningsWidgetRoot>
  );
};
